import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, TextField } from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { useListContext } from 'components/list/ListContext';
import useIsMobile from 'hooks/useIsMobile';
import SelectInput from 'components/common/SelectInput';
import { getGroups, getRoles } from 'services/gqlQueries';

const STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'active', label: 'Active' },
  { id: 'blocked', label: 'Blocked' },
];

type NamedItem = {
  id: number;
  name: string;
};

const formatRoleLabel = (name: string) =>
  name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const AllUsersFilters = () => {
  const { addFilters } = useListContext();
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState<string | number>('all');
  const [status, setStatus] = useState<string | number>('all');
  const [groupId, setGroupId] = useState<string | number>('all');
  const [garmin, setGarmin] = useState(false);
  const [suunto, setSuunto] = useState(false);
  const [coros, setCoros] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { data: groupsData } = useQuery<{ items: NamedItem[] }>(getGroups);
  const { data: rolesData } = useQuery<{ items: NamedItem[] }>(getRoles);
  const groups = useMemo(() => groupsData?.items || [], [groupsData]);
  const roles = useMemo(() => rolesData?.items || [], [rolesData]);

  const roleOptions = useMemo(
    () => [{ id: 'all', label: 'All roles' }, ...roles.map((role) => ({ id: role.id, label: formatRoleLabel(role.name) }))],
    [roles]
  );

  const groupOptions = useMemo(
    () => [{ id: 'all', label: 'All groups' }, ...groups.map((group) => ({ id: group.id, label: group.name }))],
    [groups]
  );

  const applyFilters = (next: {
    search?: string;
    roleId?: string | number;
    status?: string | number;
    groupId?: string | number;
    garmin?: boolean;
    suunto?: boolean;
    coros?: boolean;
  }) => {
    if (typeof addFilters !== 'function') {
      return;
    }

    const nextSearch = next.search ?? search;
    const nextRoleId = next.roleId ?? roleId;
    const nextStatus = next.status ?? status;
    const nextGroupId = next.groupId ?? groupId;
    const nextGarmin = next.garmin ?? garmin;
    const nextSuunto = next.suunto ?? suunto;
    const nextCoros = next.coros ?? coros;

    addFilters({
      search: nextSearch.trim() || null,
      roleIds: nextRoleId === 'all' ? null : [Number(nextRoleId)],
      status: nextStatus === 'all' ? null : nextStatus,
      groupIds: nextGroupId === 'all' ? null : [Number(nextGroupId)],
      hasGarmin: nextGarmin || null,
      hasSuunto: nextSuunto || null,
      hasCoros: nextCoros || null
    });
  };

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchTerm(search.trim());
    }, 300);

    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce search only; selects apply immediately
  }, [search]);

  useEffect(() => {
    applyFilters({
      search: searchTerm,
      roleId,
      status,
      groupId,
      garmin,
      suunto,
      coros
    });
  }, [searchTerm, roleId, status, groupId, garmin, suunto, coros]);

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'stretch' : 'center'}
      justifyContent="flex-start"
      gap={isMobile ? 1 : 2}
      sx={{ mb: 2, flexWrap: 'wrap' }}
    >
      <TextField
        size="small"
        label="Search users"
        placeholder="Name or email"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ minWidth: isMobile ? undefined : 220, flex: isMobile ? undefined : 1, maxWidth: 360 }}
      />
      <Box sx={{ minWidth: 140, flex: isMobile ? undefined : '0 0 140px' }}>
        <SelectInput name="role" label="Role" options={roleOptions} value={roleId} onChange={(value) => setRoleId(value)} />
      </Box>
      <Box sx={{ minWidth: 150, flex: isMobile ? undefined : '0 0 150px' }}>
        <SelectInput name="status" label="Status" options={STATUS_OPTIONS} value={status} onChange={(value) => setStatus(value)} />
      </Box>
      <Box sx={{ minWidth: 160, flex: isMobile ? undefined : '0 0 160px' }}>
        <SelectInput name="group" label="Group" options={groupOptions} value={groupId} onChange={(value) => setGroupId(value)} />
      </Box>
      <FormControlLabel
        control={<Checkbox size="small" checked={garmin} onChange={(event) => setGarmin(event.target.checked)} />}
        label="Garmin"
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={suunto} onChange={(event) => setSuunto(event.target.checked)} />}
        label="Suunto"
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={coros} onChange={(event) => setCoros(event.target.checked)} />}
        label="COROS"
      />
    </Stack>
  );
};

export default AllUsersFilters;
